import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/context/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Menu, X, Download, ArrowLeft } from 'lucide-react';

// variant="portada" (por defecto) baja a las secciones de la portada.
// variant="pagina" es para las páginas que no son la portada: ahí esas anclas
// no existen, así que en su lugar hay una vuelta a la portada.
export const Navbar = ({ variant = 'portada' }) => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // El mapa de localizaciones y la agenda no se pintan en la portada de fuera
  // de temporada, así que tampoco se enlazan desde aquí.
  //
  // "Edicions anteriors" ya no sale del sitio: baja a la rejilla de ediciones,
  // que está en la propia portada justo antes del formulario.
  const links = [
    { id: 'entorn', label: t.nav.entorn },
    { id: 'edicions', label: t.nav.previous },
    { id: 'contacte', label: t.nav.contacte },
  ];
  const PROGRAM_PDF_URL = '/programa-canillo-escenic-arts-2026.pdf';

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const shellClass = `fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
    scrolled ? 'bg-[#040f0c]/90 backdrop-blur border-b border-[var(--cea-border)]' : 'bg-transparent'
  }`;

  if (variant === 'pagina') {
    return (
      <header data-testid="navbar" className={shellClass}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="group flex items-center self-start mt-2.5" data-testid="nav-logo" aria-label="Canillo Escènic Arts">
            <span className={`brand-logo ${scrolled ? 'brand-logo--small' : 'brand-logo--big'}`} />
          </Link>

          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/"
              data-testid="nav-back-home"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/75 hover:text-[var(--cea-accent)] transition-colors duration-200"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">{t.edicio.back}</span>
            </Link>
            <a
              href={PROGRAM_PDF_URL}
              target="_blank"
              rel="noreferrer"
              download
              data-testid="nav-download-program"
              aria-label={t.nav.download}
              className="group relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--cea-border)] text-white/80 hover:text-[#040f0c] hover:bg-[var(--cea-accent)] hover:border-[var(--cea-accent)] transition-colors duration-200"
            >
              <Download size={18} />
            </a>
            <LanguageSelector />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      data-testid="navbar"
      className={shellClass}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="group flex items-center self-start mt-2.5" data-testid="nav-logo" aria-label="Canillo Escènic Arts">
          <span className={`brand-logo ${scrolled ? 'brand-logo--small' : 'brand-logo--big'}`} />
        </button>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-semibold text-white/75 hover:text-[var(--cea-accent)] transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PROGRAM_PDF_URL}
            target="_blank"
            rel="noreferrer"
            download
            data-testid="nav-download-program"
            aria-label={t.nav.download}
            className="group relative hidden md:inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--cea-border)] text-white/80 hover:text-[#040f0c] hover:bg-[var(--cea-accent)] hover:border-[var(--cea-accent)] transition-colors duration-200"
          >
            <Download size={18} />
            <span className="pointer-events-none absolute top-full mt-2 right-0 whitespace-nowrap rounded-md bg-[#0b241b] border border-[var(--cea-border-strong)] px-3 py-1.5 text-xs font-semibold text-white opacity-0 translate-y-1 transition-[opacity,transform] duration-200 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0">
              {t.nav.download}
            </span>
          </a>
          <LanguageSelector />
          <button
            className="md:hidden text-white"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#040f0c]/97 border-b border-[var(--cea-border)] px-4 pb-4" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-mobile-link-${l.id}`}
              className="block w-full text-left py-3 text-base font-semibold text-white/85 border-b border-white/5 hover:text-[var(--cea-accent)]"
            >
              {l.label}
            </button>
          ))}
          <a
            href={PROGRAM_PDF_URL}
            target="_blank"
            rel="noreferrer"
            download
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-download-program"
            className="flex items-center gap-2 w-full text-left py-3 text-base font-semibold text-white/85 border-b border-white/5 hover:text-[var(--cea-accent)]"
          >
            <Download size={18} /> {t.nav.download}
          </a>
        </div>
      )}
    </header>
  );
};
